import { Wallet, Infinity as InfinityIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { BudgetProgress } from './BudgetProgress'
import {
  type BudgetSummary,
  type BudgetStatus,
  getBudgetStatus,
  BUDGET_STATUS_COLORS,
} from '@/types/budget'

export interface BudgetIndicatorProps {
  /** Budget data (null = loading, undefined = no budget info) */
  budget: BudgetSummary | null | undefined
  /** Effective budget limit in USD (null for unlimited) */
  effectiveBudgetUsd?: number | null
  /** Source of budget (role name or 'user_override') */
  source?: string
  /** Budget reset date */
  resetDate?: string
  /** Additional CSS classes */
  className?: string
}

/**
 * Compact budget indicator for header.
 * Shows usage and opens popover with details on click.
 * 
 * @example
 * ```tsx
 * <BudgetIndicator budget={currentUser?.budget} />
 * ```
 */
export function BudgetIndicator({
  budget,
  effectiveBudgetUsd,
  source,
  resetDate,
  className,
}: BudgetIndicatorProps) {
  // Loading or no data
  if (budget === null) {
    return (
      <div className={cn('flex items-center gap-1.5 text-sm text-muted-foreground', className)}>
        <Wallet className="h-4 w-4" />
        <span className="animate-pulse">...</span>
      </div>
    )
  }

  if (budget === undefined) {
    return null
  }

  const isUnlimited = budget.remainingUsd === null
  const status: BudgetStatus = getBudgetStatus(budget.percentUsed, isUnlimited)
  const colors = BUDGET_STATUS_COLORS[status]

  // Format currency
  const formatUsd = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value)
  }

  // Format date for reset
  const formatResetDate = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }

  // Days until reset
  const getDaysUntilReset = (dateStr: string) => {
    const reset = new Date(dateStr)
    const now = new Date()
    const diffMs = reset.getTime() - now.getTime()
    return Math.ceil(diffMs / (1000 * 60 * 60 * 24))
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className={cn(
            'flex items-center gap-1.5 text-sm font-medium transition-colors hover:opacity-80',
            colors.text,
            status === 'critical' && 'animate-pulse',
            className
          )}
        >
          <Wallet className="h-4 w-4" />
          {isUnlimited ? (
            <span className="flex items-center gap-1">
              <InfinityIcon className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Unlimited</span>
            </span>
          ) : (
            <span>
              {formatUsd(budget.usedUsd)} / {formatUsd(effectiveBudgetUsd ?? 0)}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72">
        <div className="space-y-3">
          <div className="font-medium">Monthly Budget</div>
          
          {isUnlimited ? (
            <div className="flex items-center gap-2 text-gray-500">
              <InfinityIcon className="h-5 w-5" />
              <span>Unlimited</span>
            </div>
          ) : (
            <BudgetProgress
              percentUsed={budget.percentUsed}
              status={status}
              size="md"
            />
          )}
          
          {/* Warning messages */}
          {status === 'warning' && (
            <div className="flex items-center gap-1.5 text-yellow-600 text-sm">
              <span>⚠️</span>
              <span>Approaching budget limit</span>
            </div>
          )}
          {status === 'critical' && (
            <div className="flex items-center gap-1.5 text-red-600 text-sm">
              <span>🔴</span>
              <span>Budget almost exhausted</span>
            </div>
          )}
          
          {/* Details */}
          <div className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Used:</span>
              <span className="font-medium">{formatUsd(budget.usedUsd)}</span>
            </div>
            {!isUnlimited && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Remaining:</span>
                <span className="font-medium">{formatUsd(budget.remainingUsd ?? 0)}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-muted-foreground">Limit:</span>
              <span className="font-medium">
                {isUnlimited ? 'No limit' : formatUsd(effectiveBudgetUsd ?? 0)}
              </span>
            </div>
            {source && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Source:</span>
                <span className="font-medium capitalize">
                  {source === 'user_override' ? 'Custom Override' : `Role (${source})`}
                </span>
              </div>
            )}
          </div>
          
          {/* Reset date */}
          {resetDate && (
            <div className="pt-2 border-t text-sm text-muted-foreground">
              Resets: {formatResetDate(resetDate)} ({getDaysUntilReset(resetDate)} days)
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
